import { useState } from "react";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { fetchUserProfile, handleSignOut } from "@/utils/auth";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";
import { UserRole } from "@/types/auth";
import { authApi } from "@/api/auth";
import { supabase } from "@/lib/supabase";

export interface AuthActionsProps {
  setLoading: (loading: boolean) => void;
}

export const useAuthActions = ({ setLoading }: AuthActionsProps) => {
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      // We're still using supabase directly for sign in
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        toast.error("Sign in failed", { 
          description: error.message || "Please check your credentials and try again" 
        });
        return { error };
      }
      
      if (data?.user) {
        setTimeout(() => {
          fetchUserProfile(data.user.id, data.user.email || '', data.user).then(
            ({ role }) => {
              if (role === 'parent') {
                window.location.href = '/parent-portal';
              }
            }
          );
        }, 0);
      }
      
      return { error: null };
    } catch (error: any) {
      const context: ErrorContext = {
        operation: 'signIn',
        additionalInfo: { email }
      };
      
      handleError(
        error,
        "Unexpected error during sign in",
        ErrorSeverity.ERROR,
        context,
        true
      );
      
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    role: UserRole = "bizadmin", 
    companyName = "", 
    firstName = "", 
    lastName = ""
  ) => {
    setLoading(true);
    try {
      console.log("useAuthActions: Starting API-based signup process");
      
      // Use the new API service for signup
      const response = await authApi.signUp(
        email, 
        password, 
        role, 
        companyName, 
        firstName, 
        lastName
      );
      
      if (response.status === 'error' || response.error) {
        console.error("useAuthActions: API signup failed:", response.error);
        console.error("useAuthActions: Error message:", response.message);
        
        toast.error("Sign up failed", { 
          description: response.message || response.error?.message || "Registration failed. Please try again." 
        });
        
        return { error: response.error };
      }
      
      // Success!
      console.log("useAuthActions: API signup succeeded:", response.data);
      toast.success("Account created successfully");
      
      return {
        user: response.data?.user,
        error: null,
        profile: response.data?.profile,
        organization: response.data?.organization
      };
    } catch (error: any) {
      const context: ErrorContext = {
        operation: 'signUp',
        additionalInfo: { email, role }
      };
      
      handleError(
        error,
        "Unexpected error during sign up",
        ErrorSeverity.ERROR,
        context,
        true
      );
      
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await handleSignOut();
      if (error) {
        const context: ErrorContext = {
          operation: 'signOut'
        };
        
        handleError(
          error,
          "Sign out failed",
          ErrorSeverity.ERROR,
          context,
          true
        );
        return { error };
      }
      return { error: null };
    } catch (error: any) {
      const context: ErrorContext = {
        operation: 'signOut'
      };
      
      handleError(
        error,
        "Unexpected error during sign out",
        ErrorSeverity.ERROR,
        context,
        true
      );
      return { error };
    } finally {
      setLoading(false);
    }
  };

  return {
    signIn,
    signUp,
    signOut
  };
};
